// The forms of the sites, brought to the device (B-73, D-483).
//
// One way to bring a form, for the two places that bring them: the day
// preparation and the refresh that runs on its own (D-225). The index says
// which form each site has on the server; only a site whose stored form
// differs travels, one document at a time, every one of them read before
// anything is written.
//
// The refresh brings the forms of the sites the device already has, and no
// other: a site that is new to the device enters with "Preparar el día",
// which brings its catalogs with it. What it brings is written together, in
// one transaction — every form of the cycle or the ones before, never a
// mixture —, and a failure halfway leaves the stored forms exactly as they
// were. A visit that has started keeps the form it froze (D-49); one that has
// not takes the new form when it is opened (app/drafts.ts).
import type { ApiClient } from '../api/client';
import type { EffectiveFormIndexEntry, EffectiveFormRead } from '../api/types';
import type { FieldcoreDb, SiteFormRecord } from '../db/database';

export const FORM_INDEX_PATH = '/api/v1/sites/effective-forms';

export function formPath(siteId: string): string {
  return `/api/v1/sites/${siteId}/effective-form`;
}

/** The forms of these entries of the index whose hash differs from the stored
 *  copy, downloaded and not written: the caller writes them with the rest of
 *  what it read. */
export async function changedForms(
  api: ApiClient,
  database: FieldcoreDb,
  entries: readonly EffectiveFormIndexEntry[],
): Promise<SiteFormRecord[]> {
  const forms: SiteFormRecord[] = [];
  for (const entry of entries) {
    const local = await database.site_forms.get(entry.site_id);
    if (local?.content_hash === entry.content_hash) continue;
    const form = await api.get<EffectiveFormRead>(formPath(entry.site_id));
    forms.push({
      site_id: form.site_id,
      content_hash: form.content_hash,
      template_version_id: form.source_template_version_id,
      schema_version: form.schema_version,
      computed_at: form.computed_at,
      document: form.document,
      stored_at: Date.now(),
    });
  }
  return forms;
}

/** The refresh's half (B-73): the index read once, and the forms of the sites
 *  the device has whose hash changed, written together. With no form on the
 *  device there is nothing to compare and nothing is read. Rejects when a read
 *  fails, having written nothing; the number of forms it wrote otherwise. */
export async function refreshForms(api: ApiClient, database: FieldcoreDb): Promise<number> {
  const held = new Set(await database.site_forms.toCollection().primaryKeys());
  if (held.size === 0) return 0;
  const index = await api.get<EffectiveFormIndexEntry[]>(FORM_INDEX_PATH);
  const forms = await changedForms(
    api,
    database,
    index.filter((entry) => held.has(entry.site_id)),
  );
  if (forms.length === 0) return 0;
  return database.transaction('rw', database.site_forms, async () => {
    // A day prepared meanwhile may have let a site go: only the sites the
    // device still has take the form this cycle read.
    const still = new Set(await database.site_forms.toCollection().primaryKeys());
    const kept = forms.filter((form) => still.has(form.site_id));
    await database.site_forms.bulkPut(kept);
    return kept.length;
  });
}
